import React from 'react';
import { Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import ErrorBoundary from './components/ErrorBoundary';
import LicenseNotice from './components/LicenseNotice';
import AgentDock from './components/AgentDock';
import CopilotPanel from './components/CopilotPanel';
import Home from './pages/Home';
import ItemDetail from './pages/ItemDetail';
import Jobs from './pages/Jobs';
import Pipelines from './pages/Pipelines';
import PipelineEditor from './pages/PipelineEditor';
import Review from './pages/Review';
import Agents from './pages/Agents';
import Team from './pages/Team';
import OAuthAuthorize from './pages/OAuthAuthorize';
import DropUpload from './pages/DropUpload';
import Landing from './pages/Landing';
import DevConsole from './pages/DevConsole';
import CostReport from './pages/CostReport';
import Mail from './pages/Mail/Mail';
import Login from './components/Login';
import AuthShell from './components/AuthShell';
import { useAuth } from './AuthContext';
import { initDevCapture } from './dev/devContext';
// Namespace import: a layer written before a seam existed (e.g. no
// `extSite` export) must still build, or it can never pick up a kit release.
import * as ext from './ext';

// Computed access on purpose: CRA's strict export presence turns a static
// `ext.extSite` on a missing export into a build error.
const extExport = (name, dflt) => (name in ext ? ext[name] : dflt);
const extRoutes = extExport('extRoutes', []) || [];
const extSite = extExport('extSite', null) || null;
import { TEMPLATE_ACTIVE, TemplateHome, TemplateDetail } from './templates/Site';
import { APP_NAME, kitEnv } from './data/kitEnv';
import { useNativeShell } from './data/native';

// Console/network ring buffer starts with the app so Dev Console session
// captures have history to attach (in-memory only; scrubbed before any
// prompt).
initDevCapture();

// Optional dual-host mode: set REACT_APP_MARKETING_HOST to the marketing
// domain (e.g. example.com) to serve a public marketing shell there while
// the app lives on its other host(s) (e.g. app.example.com). Unset
// (default) = the app renders everywhere, exactly as before.
const MARKETING_HOST = process.env.REACT_APP_MARKETING_HOST || '';
const IS_MARKETING_HOST =
  MARKETING_HOST && window.location.hostname === MARKETING_HOST;

// Optional public chat: REACT_APP_PUBLIC_CHAT=1 renders the agent dock signed out
// (it signs visitors in anonymously; the backend's audience gating limits
// them to public corpus content). Unset (default) = members-only.
// Runtime env.js PUBLIC_CHAT (set by provision/claim for starter-site
// instances) wins over the build-time flag — pre-built kit images can't be
// rebuilt per instance.
const PUBLIC_CHAT = kitEnv('PUBLIC_CHAT', process.env.REACT_APP_PUBLIC_CHAT || '') === '1';

// A layer-owned public site (ext extSite) or an APP_TYPE starter template
// takes '/' — either way the kit workspace lives at /app.
export const SITE_OWNS_HOME = TEMPLATE_ACTIVE || !!extSite;
const extSiteOwns = (pathname) => !!extSite
  && (pathname === '/' || (extSite.prefixes || []).some((p) => pathname === p || pathname.startsWith(`${p}/`)));

// Launched from an iOS/Android home-screen icon (standalone display mode).
// REACT_APP_PWA_START deep-links those launches to a start route (covers
// icons added before a manifest start_url change, or platforms that ignore
// start_url). Unset (default) = no redirect.
const PWA_START = process.env.REACT_APP_PWA_START || '';
const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone === true;

// App shell pattern: public routes render freely, admin routes are wrapped in
// RequireAuth, and every route body sits inside an ErrorBoundary keyed by
// pathname so one page crashing never takes down navigation.
function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="cs-auth-wrap">Loading…</div>;
  // Anonymous sessions (created by the public chat agent) don't count.
  // Send them to /login with the deep link preserved so sign-in returns
  // them to the page they asked for (not the home list).
  if (!user || user.isAnonymous) {
    const next = location.pathname + location.search;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return children;
}

// Where a signed-in member lands after /login: the `next` deep link when it
// is a same-origin path, else the workspace home.
function afterLogin(search) {
  const next = new URLSearchParams(search).get('next') || '';
  if (next.startsWith('/') && !next.startsWith('//')) return next;
  // An internal layer app IS the members' home; a public site keeps /app.
  return SITE_OWNS_HOME && !extSite?.internal ? '/app' : '/';
}

function NotFound() {
  return (
    <div className="cs-page">
      <div className="cs-empty" style={{ marginTop: 40 }}>
        <h2 style={{ marginTop: 0 }}>Page not found</h2>
        <p className="cs-help">There is nothing at this address.</p>
        <Link to={SITE_OWNS_HOME ? '/app' : '/'} className="cs-btn cs-btn-primary">Back to items</Link>
      </div>
    </div>
  );
}

export default function App() {
  const { user } = useAuth();
  const member = user && !user.isAnonymous;
  const location = useLocation();
  const navigate = useNavigate();
  // One brand everywhere: the tab title follows the instance name.
  React.useEffect(() => { document.title = APP_NAME; }, []);
  // Inside the iOS/Android/desktop shells: deep links → routes, external
  // links → system browser, <html data-native> for safe-area CSS (data/native.js).
  useNativeShell(navigate);

  // Public guest-upload route is reachable without signing in — checked before
  // anything else (no navbar, no auth) so contributors with a link can upload.
  const isDropRoute = /^\/u\//.test(window.location.pathname);
  if (isDropRoute) {
    return (
      <ErrorBoundary key={location.pathname}>
        <Routes>
          <Route path="/u/:token" element={<DropUpload />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ErrorBoundary>
    );
  }

  // Home-screen app launches land on the configured start route.
  if (PWA_START && isStandalone() && location.pathname === '/') {
    return <Navigate to={PWA_START} replace />;
  }

  // ---- Layer-owned public site (frontend/src/ext extSite) ----
  // The layer's own front-of-house: bare shell, its routes, the public agent
  // dock. Members typing the bare host still land in the workspace (/app),
  // like the starter template below; ?site=1 shows them the site. With
  // `extSite.internal` the layer IS the members' app (an ops console, a
  // back office): '/' stays theirs and the dock serves them with voice.
  // A signed-in member typing the bare host wants their workspace (/app),
  // not the public front page (?site=1 shows it). The redirect renders
  // INSIDE the workspace tree below: returning a bare <Navigate> here
  // unmounted the agent dock for a frame and closed the live voice session
  // every time NoVA navigated "home" ([[navigate-home-killed-the-voice]]).
  const memberHomeRedirect = member && location.pathname === '/' && !location.search.includes('site=1')
    && ((!!extSite && !extSite.internal) || TEMPLATE_ACTIVE);
  if (extSiteOwns(location.pathname) && !memberHomeRedirect) {
    return (
      <>
        <a className="cs-skip" href="#main">Skip to content</a>
        <ErrorBoundary key={location.pathname}>
          <main id="main">
            <Routes>
              {extSite.routes.map((r) => (
                <Route
                  key={r.path}
                  path={r.path}
                  element={r.public === false ? <RequireAuth>{r.element}</RequireAuth> : r.element}
                />
              ))}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </ErrorBoundary>
        {(PUBLIC_CHAT || (member && extSite.internal)) && extSite.agentDock !== false && <AgentDock voiceEnabled={member ? true : extSite.publicVoice === true} />}
      </>
    );
  }

  // ---- Starter site (instance stamped with an APP_TYPE) ----
  // The public front-of-house: '/' and '/p/:id' render the template chassis
  // (templates/Site.js, driven by the public config 'site' row) for anonymous
  // visitors. The app itself moves behind the nav's "Open app" link (/app);
  // every other route is untouched. No APP_TYPE (or 'blank') = the classic
  // items-demo home, exactly as before.
  const isSiteRoute = TEMPLATE_ACTIVE
    && (location.pathname === '/' || /^\/p\//.test(location.pathname));
  if (isSiteRoute && !memberHomeRedirect) {
    return (
      <>
        <a className="cs-skip" href="#main">Skip to content</a>
        <ErrorBoundary key={location.pathname}>
          <main id="main">
            <Routes>
              <Route path="/" element={<TemplateHome />} />
              <Route path="/p/:id" element={<TemplateDetail />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </ErrorBoundary>
        {PUBLIC_CHAT && <AgentDock voiceEnabled={false} />}
      </>
    );
  }

  // ---- Marketing site (only when REACT_APP_MARKETING_HOST matches) ----
  if (IS_MARKETING_HOST) {
    return (
      <>
        <a className="cs-skip" href="#main">Skip to content</a>
        <ErrorBoundary key={location.pathname}>
          <main id="main">
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </ErrorBoundary>
        {PUBLIC_CHAT && <AgentDock voiceEnabled={false} />}
      </>
    );
  }

  return (
    <LicenseNotice>
      <a className="cs-skip" href="#main">Skip to content</a>
      <Navbar />
      <ErrorBoundary key={location.pathname}>
        <main id="main">
          <Routes>
            {/* ---- Public ---- */}
            <Route path="/" element={memberHomeRedirect ? <Navigate to="/app" replace /> : <Home />} />
            {/* On template instances the workspace home lives at /app (the
                starter site owns '/'); harmless alias otherwise. */}
            <Route path="/app" element={<RequireAuth><Home /></RequireAuth>} />
            <Route
              path="/login"
              element={member && !new URLSearchParams(location.search).has('reset')
                ? <Navigate to={afterLogin(location.search)} replace />
                : <AuthShell><Login /></AuthShell>}
            />

            {/* ---- Admin-only (sign-in required) ---- */}
            <Route path="/items/:id" element={<RequireAuth><ItemDetail /></RequireAuth>} />
            <Route path="/jobs" element={<RequireAuth><Jobs /></RequireAuth>} />

            {/* Agent stack: nQube pipelines + HITL review + embedded agents */}
            <Route path="/pipelines" element={<RequireAuth><Pipelines /></RequireAuth>} />
            <Route path="/pipelines/:pipelineId" element={<RequireAuth><PipelineEditor /></RequireAuth>} />
            <Route path="/review" element={<RequireAuth><Review /></RequireAuth>} />
            <Route path="/agents" element={<RequireAuth><Agents /></RequireAuth>} />
            {/* Tenancy: org members, workspaces, seats (org admins) */}
            <Route path="/team" element={<RequireAuth><Team /></RequireAuth>} />
            {/* MCP: OAuth consent for AI clients (api/oauthServer.js) */}
            <Route path="/oauth/authorize" element={<RequireAuth><OAuthAuthorize /></RequireAuth>} />
            {/* Email service: the inbox on the app's domain (pages/Mail) */}
            <Route path="/mail" element={<RequireAuth><Mail /></RequireAuth>} />
            <Route path="/mail/:mailboxId" element={<RequireAuth><Mail /></RequireAuth>} />
            <Route path="/mail/:mailboxId/:threadId" element={<RequireAuth><Mail /></RequireAuth>} />

            {/* Dev Console + Cost Report (internal claim gated inside the pages) */}
            <Route path="/dev" element={<RequireAuth><DevConsole /></RequireAuth>} />
            <Route path="/dev/costs" element={<RequireAuth><CostReport /></RequireAuth>} />

            {/* Layer extension routes (frontend/src/ext/ is layer-owned —
                empty in the bare kit; see docs/layer-contract.md) */}
            {extRoutes.map((r) => (
              <Route
                key={r.path}
                path={r.path}
                element={r.public ? r.element : <RequireAuth>{r.element}</RequireAuth>}
              />
            ))}

            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
      </ErrorBoundary>

      {/* The agent dock: one surface for realtime voice + text chat (RAG
          with citations). Voice stays members-only (every session mints a
          paid realtime-provider token — an abuse vector on a public page);
          with REACT_APP_PUBLIC_CHAT=1 chat also serves signed-out visitors
          via anonymous auth. */}
      {(member || PUBLIC_CHAT) && <AgentDock voiceEnabled={member} />}

      {/* The agentOS copilot (lower left): plan-gated build/run agent driven
          by the aiMessages queue — members only (it wields write tools). */}
      {member && <CopilotPanel />}
    </LicenseNotice>
  );
}
